import { Box, Tooltip, Typography } from '@mui/material';
import { colors } from '../theme';
import { PREMISES_STYLE } from './premisesStyle';

// Shared premises (ON / OFF / NA) badge — small colored chip rendered
// next to an account name across the dashboard (Follow-Up Tracker,
// Top Accounts, Growth & Decline). Style table lives in
// ./premisesStyle.ts so non-component consumers can import the colors
// without tripping React Fast Refresh.
//
// NULL premises_type renders nothing — no signal to show yet.

interface PremisesBadgeProps {
  premises: string | null;
  // When true the account is an online retailer — a distinct channel
  // that's flagged as ONLINE instead of its physical premise (it's
  // carved out of OFF in the Premises Mix, so we label it the same way).
  online?: boolean;
}

export function PremisesBadge({ premises, online = false }: PremisesBadgeProps) {
  const key = online ? 'ONLINE' : premises;
  if (!key) return null;
  const style = PREMISES_STYLE[key];
  if (!style) return null;
  return (
    <Tooltip
      placement="top"
      arrow
      title={
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: style.color, mb: 0.25 }}>
            Premises · {style.label}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.5 }}>
            {style.description}
          </Typography>
        </Box>
      }
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            px: 1.75,
            py: 1.25,
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            maxWidth: 280,
          },
        },
        arrow: {
          sx: { color: '#fff', '&::before': { border: `1px solid ${colors.border}` } },
        },
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: '0.08em',
          px: 0.6,
          py: 0.15,
          borderRadius: 0.75,
          color: style.color,
          bgcolor: style.bg,
          border: `1px solid ${style.border}`,
          cursor: 'help',
          flexShrink: 0,
          lineHeight: 1.4,
          transition: 'transform 0.12s',
          '&:hover': { transform: 'translateY(-1px)' },
        }}
      >
        {style.label}
      </Box>
    </Tooltip>
  );
}
